<template>
    <div class="wrap">
        <myHead></myHead>
        <div class="wrap_body">
            <div class="notification-page">
                <div class="center">
                    <div class="page-header">
                        <!-- 连接状态指示器 -->
                        <div class="connection-status">
                            <el-tag
                                :type="statusTagType"
                                size="small"
                                effect="plain"
                            >
                                <i :class="statusIcon"></i>
                                {{ statusText }}
                            </el-tag>
                        </div>
                        
                        <div class="page-title">
                            <h1>消息中心</h1>
                        </div>
                    </div>
                    
                    <!-- 筛选栏 -->
                    <div class="filter-bar">
                        <div class="filter-tabs">
                            <div 
                                :class="['filter-tab', { active: currentType === '' }]"
                                @click="handleTypeChange('')"
                            >
                                全部
                                <span class="tab-count">{{ totalCount }}</span>
                            </div>
                            <div 
                                v-for="type in notificationTypes"
                                :key="type.value"
                                :class="['filter-tab', { active: currentType === type.value }]"
                                @click="handleTypeChange(type.value)"
                            >
                                {{ type.label }}
                                <span class="tab-count">{{ getTypeCount(type.value) }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 通知列表 -->
                    <div class="notification-list" v-loading="loading">
                        <template v-if="filteredNotifications.length > 0">
                            <div 
                                v-for="item in filteredNotifications" 
                                :key="item.id"
                                class="notification-card"
                            >
                                <!-- 左侧图标 -->
                                <div :class="['card-icon', `type-${item.type}`]">
                                    <i :class="getTypeIcon(item.type)"></i>
                                </div>
                                
                                <!-- 中间内容 -->
                                <div class="card-content">
                                    <div class="card-header">
                                        <span class="card-title">{{ item.title }}</span>
                                        <span :class="['type-badge', `badge-${item.type}`]">{{ getTypeLabel(item.type) }}</span>
                                    </div>
                                    
                                    <div class="card-body">
                                        <p class="card-message">{{ item.content || item.message }}</p>
                                        
                                        <!-- 导入进度特殊展示 -->
                                        <div v-if="item.type === 'import_progress' && item.progress !== undefined" class="progress-section">
                                            <el-progress 
                                                :percentage="item.progress" 
                                                :status="item.progress >= 100 ? 'success' : ''"
                                            ></el-progress>
                                            <span class="progress-text">{{ item.progress }}%</span>
                                        </div>
                                        
                                        <!-- 额外信息展示 -->
                                        <div v-if="item.extra || item.data" class="card-extra">
                                            <template v-if="item.type === 'task'">
                                                <span v-if="item.data && item.data.reward_points" class="reward-points">
                                                    <i class="el-icon-trophy"></i>
                                                    奖励积分: {{ item.data.reward_points }}
                                                </span>
                                            </template>
                                            <template v-if="item.type === 'system' && item.data">
                                                <span v-if="item.data.shop_name" class="shop-info">
                                                    <i class="el-icon-shop"></i>
                                                    店铺: {{ item.data.shop_name }}
                                                </span>
                                                <span v-if="item.data.expire_date" class="expire-date">
                                                    <i class="el-icon-time"></i>
                                                    到期时间: {{ item.data.expire_date }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                    
                                    <div class="card-footer">
                                        <span class="card-time">
                                            <i class="el-icon-time"></i>
                                            {{ formatTime(item.created_at || item.create_time) }}
                                        </span>
                                        <div class="card-actions">
                                            <el-button 
                                                v-if="item.link || item.url || item.type === 'import_progress' || item.type === 'task'"
                                                type="text" 
                                                size="mini"
                                                @click="handleViewDetail(item)"
                                            >
                                                查看详情
                                            </el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        
                        <!-- 空状态 -->
                        <div v-else class="empty-state">
                            <i class="el-icon-bell"></i>
                            <h3>暂无通知消息</h3>
                            <p>当有新消息时，会在这里显示</p>
                        </div>
                    </div>
                    
                    <!-- 分页 -->
                    <div class="pagination-wrap" v-if="pagination.total > pagination.pageSize">
                        <el-pagination
                            background
                            layout="prev, pager, next, jumper"
                            :total="pagination.total"
                            :page-size="pagination.pageSize"
                            :current-page.sync="pagination.page"
                            @current-change="handlePageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <myFoot></myFoot>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { NotificationType, NotificationTypeLabel } from '@/store/modules/main/notification'
import myHead from '@/components/myHead/myhead'
import myFoot from '@/components/myFoot/myfoot'

export default {
    name: 'NotificationPage',
    
    components: {
        myHead,
        myFoot
    },
    
    data() {
        return {
            currentType: '',
            notificationTypes: [
                { value: NotificationType.IMPORT_PROGRESS, label: '导入进度' },
                { value: NotificationType.SYSTEM, label: '系统消息' },
                { value: NotificationType.TASK, label: '任务通知' }
            ]
        }
    },
    
    computed: {
        ...mapState('notification', ['notifications', 'loading', 'pagination']),
        ...mapGetters('notification', ['wsState', 'isWSConnected']),
        
        totalCount() {
            return this.notifications.length
        },
        
        // 连接状态标签类型
        statusTagType() {
            const map = {
                connected: 'success',
                connecting: 'info',
                reconnecting: 'warning',
                disconnected: 'info',
                failed: 'danger'
            }
            return map[this.wsState] || 'info'
        },
        
        // 连接状态图标
        statusIcon() {
            const map = {
                connected: 'el-icon-success',
                connecting: 'el-icon-loading',
                reconnecting: 'el-icon-loading',
                disconnected: 'el-icon-warning',
                failed: 'el-icon-error'
            }
            return map[this.wsState] || 'el-icon-info'
        },
        
        // 连接状态文本
        statusText() {
            const map = {
                connected: '实时通知已连接',
                connecting: '连接中...',
                reconnecting: '重连中...',
                disconnected: '未连接',
                failed: '连接失败'
            }
            return map[this.wsState] || '未知状态'
        },
        
        filteredNotifications() {
            let list = this.notifications
            
            // 按类型筛选
            if (this.currentType) {
                list = list.filter(n => n.type === this.currentType)
            }
            
            return list
        }
    },
    
    created() {
        this.fetchNotifications({ page: 1 })
        this.markAllAsRead()
    },
    
    methods: {
        ...mapActions('notification', [
            'fetchNotifications',
            'markAllAsRead'
        ]),
        
        // 获取类型图标
        getTypeIcon(type) {
            const icons = {
                'import_progress': 'el-icon-upload',
                'system': 'el-icon-setting',
                'task': 'el-icon-time'
            }
            return icons[type] || 'el-icon-message'
        },
        
        // 获取类型标签
        getTypeLabel(type) {
            return NotificationTypeLabel[type] || '通知'
        },
        
        // 获取类型计数
        getTypeCount(type) {
            return this.notifications.filter(n => n.type === type).length
        },
        
        // 格式化时间
        formatTime(timeStr) {
            if (!timeStr) return ''
            
            let time
            // 处理时间戳（秒或毫秒）
            if (typeof timeStr === 'number') {
                // 如果是秒级时间戳（小于10000000000），转换为毫秒
                time = new Date(timeStr < 10000000000 ? timeStr * 1000 : timeStr)
            } else {
                time = new Date(timeStr)
            }
            
            // 检查日期是否有效
            if (isNaN(time.getTime()) || time.getFullYear() < 2000) {
                return ''
            }
            
            const now = new Date()
            const diff = now - time
            
            // 未来时间或负数
            if (diff < 0) {
                return '刚刚'
            }
            
            // 1分钟内
            if (diff < 60 * 1000) {
                return '刚刚'
            }
            // 1小时内
            if (diff < 60 * 60 * 1000) {
                return Math.floor(diff / (60 * 1000)) + '分钟前'
            }
            // 24小时内
            if (diff < 24 * 60 * 60 * 1000) {
                return Math.floor(diff / (60 * 60 * 1000)) + '小时前'
            }
            // 7天内
            if (diff < 7 * 24 * 60 * 60 * 1000) {
                return Math.floor(diff / (24 * 60 * 60 * 1000)) + '天前'
            }
            // 其他显示日期
            const year = time.getFullYear()
            const month = String(time.getMonth() + 1).padStart(2, '0')
            const day = String(time.getDate()).padStart(2, '0')
            const hour = String(time.getHours()).padStart(2, '0')
            const minute = String(time.getMinutes()).padStart(2, '0')
            
            if (year === now.getFullYear()) {
                return `${month}-${day} ${hour}:${minute}`
            }
            return `${year}-${month}-${day} ${hour}:${minute}`
        },
        
        // 类型筛选变更
        handleTypeChange(type) {
            this.currentType = type
        },
        
        // 查看详情
        handleViewDetail(item) {
            // 任务通知：跳转到任务大厅
            if (item.type === 'task') {
                this.$router.push('/uc_task_hall')
                return
            }
            
            // 导入进度：跳转到商品管理
            if (item.type === 'import_progress') {
                this.$router.push('/uc_goods')
                return
            }
            
            // 其他链接跳转
            if (item.link || item.url) {
                this.$router.push(item.link || item.url)
            }
        },
        
        // 分页变更
        handlePageChange(page) {
            this.fetchNotifications({ page })
        }
    }
}
</script>

<style scoped>
.notification-page {
    min-height: calc(100vh - 300px);
    padding: 30px 0;
    background: #f5f7fa;
}

.center {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.connection-status {
    order: -1;
    width: 100%;
}

.connection-status .el-tag {
    font-size: 13px;
}

.connection-status .el-tag i {
    margin-right: 4px;
}

.page-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.page-title h1 {
    font-size: 24px;
    font-weight: 600;
    color: #303133;
    margin: 0;
}

.filter-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-tabs {
    display: flex;
    gap: 8px;
}

.filter-tab {
    padding: 8px 16px;
    border-radius: 20px;
    background: #f5f7fa;
    color: #606266;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.filter-tab:hover {
    background: #e8f4ff;
    color: #409eff;
}

.filter-tab.active {
    background: #fa5757;
    color: #fff;
}

.tab-count {
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    font-size: 12px;
}

.filter-tab.active .tab-count {
    background: rgba(255, 255, 255, 0.3);
}

.notification-list {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.notification-card {
    display: flex;
    padding: 20px;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.3s;
}

.notification-card:last-child {
    border-bottom: none;
}

.notification-card:hover {
    background: #fafafa;
}

.card-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 16px;
}

.card-icon i {
    font-size: 22px;
    color: #fff;
}

.card-icon.type-import_progress {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.card-icon.type-system {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.card-icon.type-task {
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.card-content {
    flex: 1;
    min-width: 0;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.card-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.type-badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    background: #f0f0f0;
    color: #666;
}

.badge-import_progress { background: #f0f5ff; color: #2f54eb; }
.badge-system { background: #e6fffb; color: #13c2c2; }
.badge-task { background: #f6ffed; color: #52c41a; }

.card-body {
    margin-bottom: 12px;
}

.card-message {
    font-size: 14px;
    color: #606266;
    line-height: 1.6;
    margin: 0;
}

.progress-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.progress-section .el-progress {
    flex: 1;
}

.progress-text {
    font-size: 14px;
    font-weight: 600;
    color: #409eff;
}

.card-extra {
    margin-top: 8px;
    font-size: 13px;
    color: #909399;
}

.reward-points {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #f56c6c;
    font-weight: 600;
}

.reward-points i {
    color: #ffa500;
}

.shop-info,
.expire-date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-right: 12px;
}

.expire-date {
    color: #f56c6c;
    font-weight: 600;
}

.card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-time {
    font-size: 13px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;
}

.card-actions {
    display: flex;
    gap: 8px;
}

.card-actions .el-button {
    padding: 0;
}

.empty-state {
    padding: 80px 20px;
    text-align: center;
    color: #909399;
}

.empty-state i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state h3 {
    font-size: 18px;
    color: #606266;
    margin: 0 0 8px;
}

.empty-state p {
    font-size: 14px;
    margin: 0;
}

.pagination-wrap {
    display: flex;
    justify-content: center;
    padding: 24px 0;
}
</style>
